import { Col, Row, message } from 'antd';
import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  assignToGroups,
  fetchUserGroups,
  removeFromGroups,
} from '../../../api/groups';
import {
  fetchUserById,
  sendUpdateAccountEmail,
  updateUser,
  removeUserCredentialByType,
} from '../../../api/users';
import moment from 'moment';
import { FormattedMessage, useIntl } from 'react-intl';
import { EditUserForm } from '../../../components';
import { getConfig } from '../../../config/config';
import { UserWithGroupsDetails } from '../../../constants/types';
import { getParentOrigin } from '../../../utils/getKeycloakConfig';
import { StickyAlert, StyledAlert, Title } from '../../styles';

const EditUser = () => {
  const history = useHistory();
  const intl = useIntl();
  const { userId } = useParams<{ userId: string }>();

  const parentOrigin = getParentOrigin();

  const [userDetails, setUserDetails] = useState<UserWithGroupsDetails>(
    {} as UserWithGroupsDetails,
  );

  useEffect(() => {
    fetchUserById(userId)
      .then(user => {
        setUserDetails(user as UserWithGroupsDetails);
      })
      .catch((error: any) => {
        message.error(error?.data?.error);
      });
  }, [userId]);

  const onSubmit = async (formUserDetails: any) => {
    const newUserDetails = JSON.parse(JSON.stringify(formUserDetails));
    await fetchUserGroups(userId)
      .then(data => {
        const currentGroups = data.map(group => group.id);
        const newGroups = newUserDetails.groups;

        const groupsToLeave = currentGroups.filter(
          (currentGroup: any) => newGroups.indexOf(currentGroup) === -1,
        ) as string[];

        const groupsToJoin = newGroups.filter(
          (newGroup: any) => currentGroups.indexOf(newGroup) === -1,
        ) as string[];

        groupsToJoin.forEach(async (groupId: string) => {
          await assignToGroups(userId, groupId);
        });

        groupsToLeave.forEach(async (groupId: string) => {
          await removeFromGroups(userId, groupId);
        });
      })
      .catch((error: any) => {
        message.error(error?.data?.error);
      });

    delete newUserDetails['groups'];

    if (newUserDetails.sendResetLink) {
      await sendUpdateAccountEmail(userId);
    }
    delete newUserDetails['sendResetLink'];

    if (
      newUserDetails.account_end_date &&
      moment(newUserDetails.account_end_date).isValid()
    ) {
      newUserDetails['attributes'] = {
        ...(userDetails?.attributes || {}),
        account_end_date: moment(newUserDetails.account_end_date).valueOf(),
      };
    } else {
      newUserDetails['attributes'] = {
        ...(userDetails?.attributes || {}),
        account_end_date: null,
      };
    }
    delete newUserDetails['account_end_date'];

    if (newUserDetails.configure_totp) {
      if (
        !(
          userDetails?.requiredActions?.includes('CONFIGURE_TOTP') ||
          userDetails?.totp
        )
      ) {
        newUserDetails['requiredActions'] = [
          ...(userDetails.requiredActions || []),
          'CONFIGURE_TOTP',
        ];
      }
    } else {
      if (userDetails?.requiredActions?.includes('CONFIGURE_TOTP')) {
        newUserDetails['requiredActions'] = [
          ...(userDetails.requiredActions || []).filter(
            (action: string) => action !== 'CONFIGURE_TOTP',
          ),
        ];
      } else if (userDetails?.totp) {
        try {
          await removeUserCredentialByType(userId, 'otp');
        } catch (error: any) {
          message.error(error?.data?.error);
        }
      }
    }

    delete newUserDetails['configure_totp'];

    await updateUser(userId, {
      ...(userDetails || {}),
      ...newUserDetails,
    })
      .then(() => {
        message.success(
          intl.formatMessage(
            {
              id: 'User.successfullyUpdated',
            },
            {
              firstName: newUserDetails.firstName,
              lastName: newUserDetails.lastName,
            },
          ),
          5,
        );
        // Redirect to edit user page of Spark UI if parentUrl exist
        // Normal history push if not
        if (parentOrigin && window.top && window.top !== window) {
          window.top.postMessage(
            {
              type: 'redirect',
              url: `/admin/users?updatedUser=${userId}`,
            },
            parentOrigin,
          );
        } else {
          history.push(`/users?updatedUser=${userId}`);
        }
      })
      .catch((error: any) => {
        message.error(error?.data?.error);
      });
  };

  const isLoadedInIFrame = parentOrigin && window.top && window.top !== window;

  return (
    <>
      <StickyAlert
        top={getConfig().showNavigation && !isLoadedInIFrame ? 84 : 0}
      >
        <StyledAlert
          showIcon
          type="info"
          closable={false}
          banner
          message={<FormattedMessage id="User.successfullyUpdated.logout" />}
        />
      </StickyAlert>
      <Row justify="center" style={{ paddingTop: '28px' }}>
        <Col lg={14} md={18} sm={24} style={{ padding: '20px 30px' }}>
          <Title data-testid="edit-user-title">
            <FormattedMessage id="User.colum.editUser" />
          </Title>

          {userDetails.id && (
            <EditUserForm onSubmit={onSubmit} userDetails={userDetails} />
          )}
        </Col>
      </Row>
    </>
  );
};

export default EditUser;
